// ============================================================================
// CrewAhead Maritime Platform - Onboarding API Functions
// ============================================================================
// Onboarding flow: steps, progress tracking, start, skip, and status checks.

import { apiClient, handleApiError } from './client';
import { API_ENDPOINTS } from '../utils/constants';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface OnboardingStep {
  id: string;
  stepKey: string;
  stepNumber: number;
  title: string;
  description: string;
  role: string;
  isActive: boolean;
  isSkippable: boolean;
  pointsAwarded: number;
  dependsOn: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OnboardingStepWithStatus extends OnboardingStep {
  isComplete: boolean;
  isSkipped: boolean;
  canStart: boolean;
  canSkip: boolean;
}

export interface OnboardingProgress {
  status: string;
  currentStepNumber: number;
  totalSteps: number;
  stepsCompleted: string[];
  stepsSkipped: string[];
  percentageComplete: number;
  pointsEarned: number;
  nextStepKey: string | null;
  startedAt: string | null;
  completedAt: string | null;
  lastActivityAt: string | null;
}

export interface OnboardingStatusCheck {
  isOnboardingRequired: boolean;
  isComplete: boolean;
  currentStep: number;
  totalSteps: number;
  percentage: number;
  nextStepKey: string | null;
  status: string;
}

export interface ArchetypeResolution {
  archetype: string;
  archetypeLabel: string;
  requiredFields: string[];
  showSections: string[];
  needsCoc: boolean;
  qualificationRules?: unknown[] | null;
}

export interface StepSubmitData {
  stepKey: string;
  data?: Record<string, unknown>;
  saveDraftOnly?: boolean;
}

export interface StepSubmitResponse {
  detail: string;
  stepKey: string;
  progress: OnboardingProgress;
  archetype?: ArchetypeResolution | null;
}

// ─── Steps ───────────────────────────────────────────────────────────────────

/**
 * GET /onboarding/steps/
 *
 * List onboarding steps for the current user's role with completion status.
 *
 * @returns Paginated list of onboarding steps
 */
export async function getOnboardingSteps(params?: {
  page?: number;
  pageSize?: number;
}): Promise<{
  count: number;
  results: OnboardingStepWithStatus[];
  archetype?: ArchetypeResolution | null;
}> {
  try {
    const query = new URLSearchParams();
    if (params?.page !== undefined) {
      query.set('page', String(params.page));
    }
    if (params?.pageSize !== undefined) {
      query.set('page_size', String(params.pageSize));
    }
    const qs = query.toString();
    const url = `${API_ENDPOINTS.ONBOARDING.STEPS}${qs ? `?${qs}` : ''}`;
    const response = await apiClient.get<{
      count: number;
      results: OnboardingStepWithStatus[];
      archetype?: ArchetypeResolution | null;
    }>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /onboarding/steps/{id}/
 *
 * Retrieve a single onboarding step by ID.
 *
 * @param id - Step ID
 * @returns Onboarding step detail
 */
export async function getOnboardingStep(id: string): Promise<OnboardingStep> {
  try {
    const response = await apiClient.get<OnboardingStep>(
      `${API_ENDPOINTS.ONBOARDING.STEPS}${id}/`
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Progress ────────────────────────────────────────────────────────────────

/**
 * GET /onboarding/progress/
 *
 * Return current onboarding progress with step completion status.
 * Idempotent — never mutates state.
 *
 * @returns Onboarding progress with step details
 */
export async function getOnboardingProgress(): Promise<{
  status: string;
  currentStepNumber: number;
  totalSteps: number;
  stepsCompleted: string[];
  stepsSkipped: string[];
  percentageComplete: number;
  pointsEarned: number;
  nextStepKey: string | null;
  startedAt: string | null;
  completedAt: string | null;
  lastActivityAt: string | null;
  steps: OnboardingStepWithStatus[];
}> {
  try {
    const response = await apiClient.get<
      OnboardingProgress & { totalSteps: number; steps: OnboardingStepWithStatus[] }
    >(API_ENDPOINTS.ONBOARDING.PROGRESS);
    return {
      status: response.data.status,
      currentStepNumber: response.data.currentStepNumber,
      totalSteps: response.data.totalSteps,
      stepsCompleted: response.data.stepsCompleted,
      stepsSkipped: response.data.stepsSkipped,
      percentageComplete: response.data.percentageComplete,
      pointsEarned: response.data.pointsEarned,
      nextStepKey: response.data.nextStepKey,
      startedAt: response.data.startedAt,
      completedAt: response.data.completedAt,
      lastActivityAt: response.data.lastActivityAt,
      steps: response.data.steps,
    };
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /onboarding/progress/
 *
 * Submit step data and mark the step as complete.
 * Optionally saves as draft only.
 *
 * @param data - Step submission data
 * @returns Step completion response with updated progress
 */
export async function submitOnboardingStep(
  data: StepSubmitData
): Promise<StepSubmitResponse> {
  try {
    const response = await apiClient.post<StepSubmitResponse>(
      API_ENDPOINTS.ONBOARDING.PROGRESS,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Start ───────────────────────────────────────────────────────────────────

/**
 * POST /onboarding/progress/start/
 *
 * Explicitly start onboarding for the current user.
 * Idempotent — can be called multiple times safely.
 *
 * @returns Initial onboarding progress state
 */
export async function startOnboarding(): Promise<{
  status: string;
  totalSteps: number;
  currentStepNumber: number;
  nextStepKey: string | null;
  percentageComplete: number;
}> {
  try {
    const response = await apiClient.post<{
      status: string;
      total_steps: number;
      current_step_number: number;
      next_step_key: string | null;
      percentage_complete: number;
    }>(API_ENDPOINTS.ONBOARDING.START);
    return {
      status: response.data.status,
      totalSteps: response.data.total_steps,
      currentStepNumber: response.data.current_step_number,
      nextStepKey: response.data.next_step_key,
      percentageComplete: response.data.percentage_complete,
    };
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Skip ────────────────────────────────────────────────────────────────────

/**
 * POST /onboarding/progress/skip/
 *
 * Skip a skippable onboarding step.
 *
 * @param stepKey - The step key to skip
 * @returns Updated progress after skipping
 */
export async function skipOnboardingStep(stepKey: string): Promise<{
  detail: string;
  stepKey: string;
  progress: OnboardingProgress;
}> {
  try {
    const response = await apiClient.post<{
      detail: string;
      step_key: string;
      progress: OnboardingProgress;
    }>(API_ENDPOINTS.ONBOARDING.SKIP, { step_key: stepKey });
    return {
      detail: response.data.detail,
      stepKey: response.data.step_key,
      progress: response.data.progress,
    };
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Status ──────────────────────────────────────────────────────────────────

/**
 * GET /onboarding/status/
 *
 * Quick lightweight status check for frontend route guards.
 * Determines whether to redirect to onboarding flow or allow access.
 *
 * @returns Onboarding status summary
 */
export async function getOnboardingStatus(): Promise<OnboardingStatusCheck> {
  try {
    const response = await apiClient.get<OnboardingStatusCheck>(
      API_ENDPOINTS.ONBOARDING.STATUS
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Complete ────────────────────────────────────────────────────────────────

/**
 * POST /onboarding/progress/
 *
 * Convenience wrapper to mark the final onboarding step as complete
 * and finalize the onboarding process.
 *
 * @param stepKey - The final step key to complete
 * @param data - Optional final step data
 * @returns Step completion response with updated progress
 */
export async function completeOnboarding(
  stepKey: string,
  data?: Record<string, unknown>
): Promise<StepSubmitResponse> {
  return submitOnboardingStep({ stepKey, data });
}
